import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { toast } from 'sonner';
import { FileDown, Pencil, Trash2 } from 'lucide-react';
import type { Movement, MovementFilters, MovementCreateInput, MovementUpdateInput } from 'shared/schemas';
import { exportMovementsPdf } from '../../../features/movements/lib/exportMovementsPdf.ts';
import { Button } from '../../../components/ui/button.tsx';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.tsx';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet.tsx';
import { KpiCard } from '../../../features/dashboard/components/KpiCard.tsx';
import { KpiStrip } from '../../../features/dashboard/components/KpiStrip.tsx';
import { PageHeader } from '../../../components/layout/PageHeader.tsx';
import { MovementDeleteDialog } from '../../../features/movements/components/MovementDeleteDialog.tsx';
import { MovementDetailCard } from '../../../features/movements/components/MovementDetailCard.tsx';
import { MovementFiltersBar } from '../../../features/movements/components/MovementFiltersBar.tsx';
import { MovementForm } from '../../../features/movements/components/MovementForm.tsx';
import { MovementsTimeline } from '../../../features/movements/components/MovementsTimeline.tsx';
import { MovementTypeBadge } from '../../../features/movements/components/MovementTypeBadge.tsx';
import { useMovement } from '../../../features/movements/hooks/useMovement.ts';
import { useMovements } from '../../../features/movements/hooks/useMovements.ts';
import { useMovementsStats } from '../../../features/movements/hooks/useMovementsStats.ts';
import { useCreateMovement } from '../../../features/movements/hooks/useCreateMovement.ts';
import { useUpdateMovement } from '../../../features/movements/hooks/useUpdateMovement.ts';
import { usePartyBalance } from '../../../features/movements/hooks/usePartyBalance.ts';
import { formatCurrency } from '../../../lib/utils.ts';
import { STRINGS } from '../../../features/movements/movements.strings.ts';

const movementsSearchSchema = z.object({
  view: z.string().optional(),
  edit: z.string().optional(),
  new: z.boolean().optional(),
});

export const Route = createFileRoute('/_authed/movements/')({
  validateSearch: (s) => movementsSearchSchema.parse(s),
  component: MovementsPage,
});

type ActiveFilters = Partial<
  Pick<MovementFilters, 'partyId' | 'type' | 'sourceType' | 'occurredOnFrom' | 'occurredOnTo' | 'search'>
>;

function MovementSheetDetail({
  movementId,
  onEdit,
  onDelete,
}: {
  movementId: string;
  onEdit: () => void;
  onDelete: (movement: Movement) => void;
}) {
  const { data, isLoading } = useMovement(movementId);
  const movement = data?.data;

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!movement) {
    return (
      <div className="p-6 text-sm text-muted-foreground">No se encontró el movimiento.</div>
    );
  }

  return (
    <div className="overflow-y-auto p-6">
      <MovementDetailCard
        movement={movement}
        onEdit={onEdit}
        onDelete={() => onDelete(movement)}
      />
    </div>
  );
}

function MovementsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ActiveFilters>({});
  const [movementToDelete, setMovementToDelete] = useState<Movement | null>(null);

  const { view: selectedId, new: isNewOpen, edit: editId } = Route.useSearch();

  const { data, isLoading } = useMovements({
    page: 1,
    pageSize: 50,
    ...filters,
  });

  const stats = useMovementsStats();
  const partyBalanceQuery = usePartyBalance(filters.partyId);
  const createMovement = useCreateMovement();
  const updateMovement = useUpdateMovement(editId ?? '');

  const movements = data?.data ?? [];
  const selectedMovement = movements.find((m) => m.id === selectedId) ?? null;

  function openNew() {
    void navigate({ to: '/movements', search: { new: true }, replace: true });
  }

  function closeModals() {
    void navigate({ to: '/movements', search: {}, replace: true });
  }

  function handleFilterChange(next: ActiveFilters) {
    setFilters(next);
  }

  function handleCreate(formData: MovementCreateInput) {
    createMovement.mutate(formData, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_CREATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  function handleUpdate(formData: MovementUpdateInput) {
    updateMovement.mutate(formData, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_UPDATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  const editMovement = editId ? movements.find((m) => m.id === editId) : undefined;

  return (
    <div className="flex-1 overflow-auto px-3 sm:px-6 py-4 sm:py-6">
      <PageHeader
        title={STRINGS.LIST_TITLE}
        actions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={movements.length === 0}
              onClick={() => {
                const partyName = filters.partyId ? (movements.find((m) => m.partyId === filters.partyId)?.partyName ?? undefined) : undefined;
                exportMovementsPdf({ movements, filterPartyName: partyName });
              }}
            >
              <FileDown className="size-4 mr-1.5" />
              Exportar PDF
            </Button>
            <Button type="button" onClick={openNew}>
              {STRINGS.NEW_BUTTON}
            </Button>
          </div>
        }
      />

      <KpiStrip>
        <KpiCard label="Total" value={String(stats.total)} />
        <KpiCard label="Ingresos del mes" value={formatCurrency(stats.ingresosDelMes)} />
        <KpiCard label="Egresos del mes" value={formatCurrency(stats.egresosDelMes)} />
        <KpiCard label="Saldo del mes" value={formatCurrency(stats.saldoDelMes)} />
      </KpiStrip>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        {/* Sidebar filters */}
        <aside className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4">
          <p className="text-sm font-semibold">Filtros</p>
          <MovementFiltersBar
            filters={filters}
            onChange={handleFilterChange}
          />

          {/* Balance strip */}
          {filters.partyId && partyBalanceQuery.data && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              <span className="text-muted-foreground">{STRINGS.BALANCE_TITLE}: </span>
              <span className="font-semibold tabular-nums">
                {partyBalanceQuery.data.data.balance}
              </span>
            </div>
          )}
        </aside>

        {/* Timeline */}
        <div className="min-w-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <MovementsTimeline
              movements={movements}
              onSelect={(m) =>
                void navigate({ to: '/movements', search: { view: m.id }, replace: true })
              }
            />
          )}
        </div>
      </div>

      {/* Movement detail sheet */}
      <Sheet
        open={Boolean(selectedId)}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <SheetContent side="right" className="w-full max-w-lg overflow-hidden p-0">
          {selectedId && (
            <>
              <SheetHeader className="border-b border-border p-6 pb-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {selectedMovement && (
                      <MovementTypeBadge type={selectedMovement.type} />
                    )}
                    <SheetTitle className="text-base">
                      {selectedMovement?.partyName ?? STRINGS.DETAIL_TITLE}
                    </SheetTitle>
                  </div>
                  {selectedMovement?.sourceType === 'manual' && (
                    <div className="flex shrink-0 gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          void navigate({ to: '/movements', search: { edit: selectedId }, replace: true })
                        }
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          if (selectedMovement) setMovementToDelete(selectedMovement);
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </SheetHeader>

              <MovementSheetDetail
                movementId={selectedId}
                onEdit={() =>
                  void navigate({ to: '/movements', search: { edit: selectedId }, replace: true })
                }
                onDelete={(m) => setMovementToDelete(m)}
              />
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* New movement dialog */}
      <Dialog
        open={isNewOpen === true}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{STRINGS.NEW_BUTTON}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <MovementForm
              mode="create"
              formId="movement-create-form"
              onSubmit={handleCreate}
              isPending={createMovement.isPending}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModals}>
              {STRINGS.ACTION_CANCEL}
            </Button>
            <Button type="submit" form="movement-create-form" disabled={createMovement.isPending}>
              {STRINGS.ACTION_SAVE}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit movement dialog */}
      <Dialog
        open={editMovement !== null && editMovement !== undefined}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{STRINGS.EDIT_TITLE}</DialogTitle>
          </DialogHeader>
          {editMovement && (
            <>
              <DialogBody>
                <MovementForm
                  mode="edit"
                  formId="movement-edit-form"
                  initialValues={editMovement}
                  onSubmit={handleUpdate}
                  isPending={updateMovement.isPending}
                />
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeModals}>
                  {STRINGS.ACTION_CANCEL}
                </Button>
                <Button type="submit" form="movement-edit-form" disabled={updateMovement.isPending}>
                  {STRINGS.ACTION_SAVE}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <MovementDeleteDialog
        movement={movementToDelete}
        open={movementToDelete !== null}
        onClose={() => {
          setMovementToDelete(null);
          closeModals();
        }}
      />
    </div>
  );
}
